(() => {
  const panelFor = tab => document.getElementById((tab.dataset.target || tab.getAttribute('aria-controls') || '').replace(/^#/, ''));
  const groupFor = tab => tab.closest('ul, [role="tablist"]') || tab.parentElement;

  function upgrade(root = document) {
    const tabs = [...root.querySelectorAll('[data-ui-tab]')];
    for (const tab of tabs) {
      const group = groupFor(tab);
      group.setAttribute('role', 'tablist');
      if (!group.hasAttribute('aria-label')) {
        const heading = group.closest('product-tab')
          ? document.querySelector('.productView-title, h1')
          : group.closest('section, [id^="shopify-section"]')?.querySelector('h2, h3');
        group.setAttribute('aria-label', heading?.textContent.trim() || document.title);
      }
      const panel = panelFor(tab);
      if (panel) {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tab.id);
      }
    }
    for (const group of new Set(tabs.map(groupFor))) {
      const children = [...group.querySelectorAll('[data-ui-tab]')];
      if (!children.some(tab => tab.getAttribute('aria-selected') === 'true') && children[0]) {
        children[0].setAttribute('aria-selected', 'true');
        children[0].tabIndex = 0;
      }
    }
  }

  function select(tab) {
    for (const candidate of groupFor(tab).querySelectorAll('[data-ui-tab]')) {
      const active = candidate === tab;
      candidate.setAttribute('aria-selected', String(active));
      candidate.tabIndex = active ? 0 : -1;
      candidate.classList.toggle('active', active);
      if (candidate.classList.contains('tab-title')) candidate.classList.toggle('is-open', active);
      const panel = panelFor(candidate);
      if (panel) {
        panel.hidden = !active;
        panel.classList.toggle('active', active);
      }
    }
  }

  function closeContactMenus(except) {
    document.querySelectorAll('[data-ui-contact-menu][open]').forEach(menu => {
      if (menu !== except) menu.open = false;
    });
  }
  document.addEventListener('pointerdown', event => closeContactMenus(event.target.closest('[data-ui-contact-menu]')));
  document.addEventListener('toggle', event => {
    if (event.target.matches('[data-ui-contact-menu]') && event.target.open) closeContactMenus(event.target);
  }, true);
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    const menu = event.target.closest('[data-ui-contact-menu][open]');
    if (!menu) return;
    event.preventDefault();
    menu.open = false;
    menu.querySelector('summary').focus();
  });
  document.addEventListener('focusout', event => {
    const menu = event.target.closest('[data-ui-contact-menu]');
    if (menu && event.relatedTarget && !menu.contains(event.relatedTarget)) menu.open = false;
  });

  const disclosureAnimations = new WeakMap();
  function toggleDisclosure(trigger, panel) {
    const expanded = trigger.getAttribute('aria-expanded') !== 'true';
    const startHeight = panel.hidden ? 0 : panel.getBoundingClientRect().height;
    disclosureAnimations.get(panel)?.cancel();
    disclosureAnimations.delete(panel);
    trigger.setAttribute('aria-expanded', String(expanded));
    trigger.classList.toggle('collapsed', !expanded);
    panel.hidden = false;
    panel.inert = !expanded;
    panel.setAttribute('aria-hidden', String(!expanded));
    if (!expanded && panel.contains(document.activeElement)) trigger.focus();
    const duration = matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : parseFloat(getComputedStyle(panel).getPropertyValue('--alwan-duration')) || 180;
    if (panel.matches('.ui-accordion__panel') && duration && panel.animate) {
      const animation = panel.animate([{height: `${startHeight}px`}, {height: expanded ? `${panel.scrollHeight}px` : '0px'}], {duration, easing: 'ease'});
      disclosureAnimations.set(panel, animation);
      animation.finished.then(() => {
        if (disclosureAnimations.get(panel) !== animation) return;
        disclosureAnimations.delete(panel);
        panel.hidden = !expanded;
      }).catch(() => {});
    } else {
      panel.hidden = !expanded;
    }
  }

  document.addEventListener('click', event => {
    const tab = event.target.closest('[data-ui-tab]');
    if (tab) {
      event.preventDefault();
      // After the click has run its course: Ella's tab handlers skip a tab that is already active, and
      // the product tabs only load their products from that handler.
      setTimeout(() => select(tab));
    }
    const trigger = event.target.closest('[data-ui-disclosure]');
    if (!trigger) return;
    const item = trigger.closest('[data-ui-accordion]');
    if (!item && trigger.closest('faqs-item')) return;
    const panel = item?.querySelector('.ui-accordion__panel') || document.getElementById(trigger.getAttribute('aria-controls'));
    if (panel) toggleDisclosure(trigger, panel);
  }, true);

  document.addEventListener('keydown', event => {
    const tab = event.target.closest('[data-ui-tab]');
    if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const tabs = [...groupFor(tab).querySelectorAll('[data-ui-tab]')];
    const index = tabs.indexOf(tab);
    const rtl = getComputedStyle(tab).direction === 'rtl';
    const delta = (event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + delta + tabs.length) % tabs.length;
    event.preventDefault();
    tabs[next].focus();
    tabs[next].click();
  });

  document.addEventListener('change', event => {
    if (!event.target.matches('[data-ui-faq-filter]')) return;
    const faq = event.target.closest('faqs-item') || event.target.closest('[data-section-type="faqs"]');
    faq?.querySelectorAll('.faqs-paragraph').forEach(panel => {
      panel.classList.toggle('is-hidden', Boolean(event.target.value) && panel.id !== event.target.value);
    });
  });

  // Variant selector: a sold-out value cannot be chosen. Ella's variant scripts mark availability on each value's
  // label (soldout / unavailable) as the other options change; its radio follows, except the chosen one.
  function syncVariantValue(label) {
    const input = label.previousElementSibling;
    if (!input || input.type !== 'radio') return;
    const off = label.classList.contains('soldout') || label.classList.contains('unavailable');
    input.disabled = off && !input.checked;
    label.setAttribute('aria-disabled', String(off));
  }
  const syncVariantSelectors = root => root.querySelectorAll?.('.ui-variant-selector__value').forEach(syncVariantValue);
  document.addEventListener('change', event => {
    const selector = event.target.closest?.('.ui-variant-selector');
    if (selector) requestAnimationFrame(() => syncVariantSelectors(selector));
  });

  // Cart lines (ui-cart-item): Ella's cart scripts draw the cart again after each change. Meanwhile the line shows
  // as updating; then focus goes back to the same control of the same line (after a removal, to the next line's
  // title, or the cart's heading when it is empty) and a status message names the change.
  let cartPending = null;
  function cartChangeStarted(control, removed) {
    const line = control.closest('.ui-cart-item');
    if (!line) return;
    const scope = line.closest('#halo-cart-sidebar, #main-cart-items');
    const active = line.contains(document.activeElement) ? document.activeElement : control;
    const role = removed ? 'remove' : active.matches('.minus') ? 'minus' : active.matches('.plus') ? 'plus' : 'input';
    cartPending = {scope, key: line.dataset.line, index: [...(scope || document).querySelectorAll('.ui-cart-item')].indexOf(line),
      role, removed, message: removed ? line.dataset.uiRemoved : line.dataset.uiUpdated};
    line.classList.add('is-updating');
    line.setAttribute('aria-busy', 'true');
    // If the update fails the line is not drawn again: let it be used again.
    setTimeout(() => { line.classList.remove('is-updating'); line.removeAttribute('aria-busy'); }, 10000);
  }
  function announce(scope, text) {
    if (!text) return;
    let status = scope.querySelector(':scope > .ui-status');
    if (!status) {
      status = Object.assign(document.createElement('div'), {className: 'ui-status alwan-visually-hidden'});
      status.setAttribute('role', 'status');
      scope.append(status);
    }
    status.textContent = '';
    setTimeout(() => { status.textContent = text; }, 60);
  }
  document.addEventListener('change', event => {
    if (event.target.matches?.('.ui-cart-item [data-cart-quantity]')) cartChangeStarted(event.target, false);
  }, true);
  document.addEventListener('click', event => {
    const remove = event.target.closest?.('.ui-cart-item [data-cart-remove]');
    if (remove) cartChangeStarted(remove, true);
  }, true);
  document.addEventListener('cart-update', () => {
    const pending = cartPending;
    cartPending = null;
    if (!pending) return;
    requestAnimationFrame(() => {
      const scope = pending.scope?.isConnected ? pending.scope : document.body;
      const lines = [...scope.querySelectorAll('.ui-cart-item')];
      const line = lines.find(candidate => candidate.dataset.line === pending.key);
      let target;
      if (line && !pending.removed) {
        target = {minus: '.minus', plus: '.plus'}[pending.role];
        target = (target && line.querySelector(target)) || line.querySelector('[data-cart-quantity]');
      } else if (line) {
        target = line.querySelector('[data-cart-remove]');
      } else {
        target = lines[Math.min(pending.index, lines.length - 1)]?.querySelector('.ui-cart-item__title');
        if (!target) {
          target = scope.querySelector('h1, h2, h3, .halo-sidebar-title, .previewCartTitle');
          target?.setAttribute('tabindex', '-1');
        }
      }
      target?.focus({preventScroll: false});
      // The drawer is a dialog, so its message stays inside it; the cart page redraws its content, so use the body.
      announce(scope.closest('#halo-cart-sidebar') || document.body, pending.message);
    });
  });

  // Dialog (ui-dialog, and sheets marked data-ui-dialog): Ella's scripts open and close each one with a class, on
  // <body> (data-ui-dialog-open) or on the dialog itself (data-ui-dialog-self). On open, focus moves in (the title,
  // or data-ui-dialog-focus) and stays inside; Escape closes through the close control so Ella's handler runs; the
  // rest of the page is inert and does not scroll. On close, focus goes back to the control that opened it.
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const openDialogs = [];
  let lastActivator = null;
  document.addEventListener('pointerdown', event => { lastActivator = event.target.closest?.(FOCUSABLE) || lastActivator; }, true);
  document.addEventListener('keydown', event => {
    if (event.key === 'Enter' || event.key === ' ') lastActivator = event.target.closest?.(FOCUSABLE) || lastActivator;
  }, true);
  const dialogIsOpen = dialog => dialog.dataset.uiDialogOpen
    ? document.body.classList.contains(dialog.dataset.uiDialogOpen)
    : dialog.classList.contains(dialog.dataset.uiDialogSelf || 'is-open');
  const visible = element => element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden';
  // Only the topmost open modal and what contains it stay live: worked out again whenever one opens or closes, so a
  // dialog opened from a drawer keeps the page inert even when the drawer closes first.
  let inertNodes = [];
  function applyInert() {
    inertNodes.forEach(node => { node.inert = false; });
    inertNodes = [];
    const top = [...openDialogs].reverse().find(entry => entry.dialog.getAttribute('aria-modal') !== 'false')?.dialog;
    if (top) {
      for (let node = top; node && node !== document.body; node = node.parentElement) {
        for (const sibling of node.parentElement.children) {
          if (sibling === node || sibling.inert || sibling.matches('script, style, link, template, .background-overlay')) continue;
          sibling.inert = true;
          inertNodes.push(sibling);
        }
      }
    }
    document.documentElement.classList.toggle('ui-dialog-locked', openDialogs.length > 0);
  }
  function openDialog(dialog) {
    if (openDialogs.some(entry => entry.dialog === dialog)) return;
    // A panel that is only a dialog while open (the phone filters, a sidebar on desktop) takes the role now.
    if (dialog.dataset.uiDialogRole === 'dynamic') { dialog.setAttribute('role', 'dialog'); dialog.setAttribute('aria-modal', 'true'); }
    const opener = lastActivator && !dialog.contains(lastActivator) ? lastActivator : document.activeElement;
    const entry = {dialog, opener};
    openDialogs.push(entry);
    applyInert();
    // Ella fades the dialog in from visibility: hidden, which cannot take focus: try again over a few frames.
    let tries = 0;
    const focusInside = () => {
      if (!openDialogs.includes(entry) || dialog.contains(document.activeElement)) return;
      const target = dialog.querySelector(dialog.dataset.uiDialogFocus || '.ui-dialog__title, .ui-drawer__title') || [...dialog.querySelectorAll(FOCUSABLE)].find(visible);
      target?.focus({preventScroll: true});
      if (!dialog.contains(document.activeElement) && ++tries < 30) requestAnimationFrame(focusInside);
    };
    requestAnimationFrame(focusInside);
  }
  function closeDialog(dialog) {
    const index = openDialogs.findIndex(entry => entry.dialog === dialog);
    if (index < 0) return;
    const [entry] = openDialogs.splice(index, 1);
    if (dialog.dataset.uiDialogRole === 'dynamic') { dialog.removeAttribute('role'); dialog.removeAttribute('aria-modal'); }
    applyInert();
    if (entry.opener?.isConnected && visible(entry.opener) && !entry.opener.closest('[inert]')) entry.opener.focus({preventScroll: true});
  }
  function syncDialogs() {
    document.querySelectorAll('[data-ui-dialog]').forEach(dialog => (dialogIsOpen(dialog) ? openDialog : closeDialog)(dialog));
    openDialogs.filter(entry => !entry.dialog.isConnected).forEach(entry => closeDialog(entry.dialog));
  }
  document.addEventListener('keydown', event => {
    const top = openDialogs[openDialogs.length - 1]?.dialog;
    if (!top) {
      if (event.key === 'Escape' && document.body.classList.contains('has-warning')) document.body.classList.remove('has-warning');
      return;
    }
    if (event.key === 'Escape') {
      const close = [...top.querySelectorAll('[data-ui-dialog-close]')].find(visible);
      if (close) { event.preventDefault(); close.click(); }
      return;
    }
    if (event.key !== 'Tab') return;
    const items = [...top.querySelectorAll(FOCUSABLE)].filter(visible);
    if (!items.length) return;
    const first = items[0], last = items[items.length - 1];
    if (!top.contains(document.activeElement)) { event.preventDefault(); (event.shiftKey ? last : first).focus(); }
    else if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });

  // Toast (ui-toast): Ella's showWarning() shows it with body.has-warning and hides it again after 3 seconds. Keep it
  // up at least 6 seconds and while the pointer or keyboard focus is on it; the shopper's own click, tap or key (the
  // close button, Escape, a click elsewhere) still closes it at once.
  const TOAST_MIN = 6000;
  let toastOpen = document.body.classList.contains('has-warning');
  let toastShownAt = toastOpen ? Date.now() : 0;
  let toastClosing = false;
  let toastTimer = null;
  let lastUserAction = 0;
  ['pointerdown', 'keydown'].forEach(type => document.addEventListener(type, () => { lastUserAction = Date.now(); }, true));
  const toastHeld = () => { const toast = document.querySelector('[data-ui-toast]'); return !!toast && (toast.matches(':hover') || toast.contains(document.activeElement)); };
  function closeToastLater() {
    clearTimeout(toastTimer);
    const wait = Math.max(TOAST_MIN - (Date.now() - toastShownAt), 0);
    toastTimer = setTimeout(function check() {
      if (toastHeld()) { toastTimer = setTimeout(check, 1000); return; }
      toastClosing = true;
      document.body.classList.remove('has-warning');
    }, wait);
  }
  function toastChanged() {
    const open = document.body.classList.contains('has-warning');
    if (open === toastOpen) return;
    toastOpen = open;
    if (open) { toastShownAt = Date.now(); toastClosing = false; closeToastLater(); return; }
    const byShopper = Date.now() - lastUserAction < 400;
    if (!toastClosing && !byShopper && (Date.now() - toastShownAt < TOAST_MIN || toastHeld())) {
      toastOpen = true;
      document.body.classList.add('has-warning');
      return;
    }
    clearTimeout(toastTimer);
    toastClosing = false;
  }

  // Alert (ui-alert): the close button hides its message.
  document.addEventListener('click', event => {
    const close = event.target.closest?.('[data-ui-alert-dismiss]');
    if (close) close.closest('.ui-alert').hidden = true;
  });

  // Load more (ui-pagination): Ella's script appends the next products to the grid and swaps in the new pagination.
  // Then focus moves to the first new product (it would drop to the page) and the new count is announced.
  let morePending = null;
  document.addEventListener('click', event => {
    if (!event.target.closest?.('.ui-pagination [data-infinite-scrolling]')) return;
    const list = document.querySelector('#CollectionProductGrid .productListing');
    morePending = list && {list, count: list.children.length};
  }, true);
  function moreLoaded() {
    const pending = morePending;
    morePending = null;
    if (!pending) return;
    const first = pending.list.children[pending.count];
    (first?.querySelector('.ui-product-card__title') || first?.querySelector('a[href]'))?.focus();
    announce(document.body, document.querySelector('#CollectionProductGrid .ui-pagination__status, #CollectionProductGrid .ui-pagination__end')?.textContent.trim());
  }

  // Offer countdown (ui-offer-countdown): one tick a second for every timer on the page, including cards added later;
  // a timer that reaches zero hides, so an open page never shows a finished offer.
  const pad = n => String(n).padStart(2, '0');
  function tickCountdowns() {
    const now = Math.floor(Date.now() / 1000);
    for (const timer of document.querySelectorAll('[data-ui-countdown]')) {
      const left = Number(timer.dataset.uiCountdown) - now;
      if (left <= 0) { timer.hidden = true; continue; }
      const showDays = left > 86400, d = Math.floor(left / 86400);
      const h = showDays ? Math.floor(left % 86400 / 3600) : Math.floor(left / 3600);
      const m = Math.floor(left % 3600 / 60), s = left % 60;
      const days = timer.querySelector('[data-ui-countdown-days]');
      if (days) days.hidden = !showDays;
      const dayNumber = timer.querySelector('[data-ui-countdown-d]');
      if (dayNumber) dayNumber.textContent = String(d);
      const clock = timer.querySelector('[data-ui-countdown-clock]');
      if (clock) clock.textContent = `${pad(h)}:${pad(m)}:${pad(s)}`;
      const set = (key, value) => { const el = timer.querySelector(`[data-ui-countdown-${key}]`); if (el) el.textContent = pad(value); };
      set('h', h); set('m', m); set('s', s);
    }
  }
  tickCountdowns();
  setInterval(tickCountdowns, 1000);

  upgrade();
  syncVariantSelectors(document);
  new MutationObserver(records => {
    let dialogsChanged = false;
    for (const record of records) {
      if (record.type === 'attributes') {
        if (record.target.matches('.ui-variant-selector__value')) syncVariantValue(record.target);
        if (record.target === document.body || record.target.matches('[data-ui-dialog]')) dialogsChanged = true;
        if (record.target === document.body) toastChanged();
        continue;
      }
      if (toastOpen && record.target.matches?.('[data-halo-warning-content]')) { toastShownAt = Date.now(); closeToastLater(); }
      if (record.removedNodes.length) dialogsChanged = true;
      if (morePending && record.target === morePending.list && record.addedNodes.length) requestAnimationFrame(moreLoaded);
      for (const node of record.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches('[data-ui-tab]') || node.querySelector('[data-ui-tab]')) upgrade(node.parentElement || node);
        if (node.matches('.ui-variant-selector') || node.querySelector('.ui-variant-selector')) syncVariantSelectors(node);
      }
    }
    if (dialogsChanged) syncDialogs();
  }).observe(document.body, {childList: true, subtree: true, attributes: true, attributeFilter: ['class']});
})();
